<template>
  <div class="relative inline-flex items-center">
    <!-- Info icon with hover effect -->
    <button
      @mouseenter="onHoverStart"
      @mouseleave="onHoverEnd"
      class="inline-flex items-center justify-center focus:outline-none transition-colors"
      aria-label="More information"
    >
      <svg 
        width="14" 
        height="14" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        stroke-width="2" 
        stroke-linecap="round" 
        stroke-linejoin="round"
        class="cursor-help transition-colors"
        :class="hover ? (isDarkMode ? 'text-gray-300' : 'text-gray-600') : (isDarkMode ? 'text-gray-400' : 'text-gray-500')"
      >
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="16" x2="12" y2="12"></line>
        <line x1="12" y1="8" x2="12.01" y2="8"></line>
      </svg>
    </button>

    <div
      v-if="visible"
      ref="tooltip"
      class="info-tooltip"
      :style="tooltipPosition"
      @click.stop
    >
      <div class="info-tooltip-content" :style="tooltipContentStyle">
        <!-- Content states -->
        <div v-if="loading" class="flex items-center justify-center space-x-2 py-2">
          <svg class="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span class="text-sm">Loading...</span>
        </div>
        <div v-else-if="error" class="text-red-500 flex items-center space-x-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span class="text-sm">{{ error }}</span>
        </div>
        <div v-else v-html="sanitizedContent"></div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import DOMPurify from 'dompurify';

export default {
  props: {
      onVisibilityChange: {
      type: Function,
      default: () => {}
    },
    
    identifier: {
      type: String,
      required: true
    },
    position: {
      type: String,
      default: 'bottom', // 'top', 'bottom', 'left', 'right'
      validator: value => ['top', 'bottom', 'left', 'right'].includes(value)
    },
    sanitizeConfig: {
      type: Object,
      default: () => ({
        ALLOWED_TAGS: ['a', 'p', 'strong', 'em', 'u', 's', 'blockquote', 'code', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'img'],
        ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'class', 'style']
      })
    }
  },
  data() {
    return {
      visible: false,
      hover: false,
      loading: false,
      error: null,
      tooltipContent: '',
    }
  },
  computed: {
    isDarkMode() {
      // Check if dark mode is active
      return document.documentElement.classList.contains('dark');
    },
    
    sanitizedContent() {
      return DOMPurify.sanitize(this.tooltipContent, this.sanitizeConfig);
    },
 
    positionClass() {
      return '';
    },
    
    tooltipPosition() {
      if (!this.visible) return {};
      
      // Get the button's position
      const button = this.$el.querySelector('button');
      if (!button) return {};
      
      const rect = button.getBoundingClientRect();
      const tooltipWidth = 300; // max-width
      const tooltipHeight = 100; // estimated
      const offset = 8;
      
      let top, left;
      
      if (this.position === 'bottom') {
        top = rect.bottom + offset;
        left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
      } else if (this.position === 'top') {
        top = rect.top - tooltipHeight - offset;
        left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
      } else if (this.position === 'left') {
        top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
        left = rect.left - tooltipWidth - offset;
      } else { // right
        top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
        left = rect.right + offset;
      }
      
      // Keep tooltip within viewport
      if (left < 10) left = 10;
      if (left + tooltipWidth > window.innerWidth - 10) {
        left = window.innerWidth - tooltipWidth - 10;
      }
      if (top < 10) top = 10;
      
      return {
        top: `${top}px`,
        left: `${left}px`,
        position: 'fixed',
        zIndex: '2147483647',
        pointerEvents: 'none',
        maxWidth: '300px',
        minWidth: '200px'
      };
    },
    tooltipContentStyle() {
      return {
        backgroundColor: '#374151',
        border: '2px solid #4b5563',
        borderRadius: '0.5rem',
        padding: '0.75rem',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)',
        backdropFilter: 'blur(12px)',
        fontSize: '0.75rem',
        lineHeight: '1.5',
        color: '#ffffff',
        opacity: '1',
        maxHeight: '400px',
        overflowY: 'auto',
        wordWrap: 'break-word',
        whiteSpace: 'normal'
      };
    },
  },
  methods: {
    // async onHoverStart() {
    //   this.hover = true;
    //   if (!this.tooltipContent && !this.loading) {
    //     await this.loadTooltip(); // fetch content on first hover
    //   }
    //   this.visible = true;
    //   this.$root.$emit('tooltip-visibility', true);
    // },
    // onHoverEnd() {
    //   this.hover = false;
    //   this.visible = false;
    //   this.$root.$emit('tooltip-visibility', false);
    // },
    async onHoverStart() {
      this.hover = true;
      
      // Load tooltip content if not already loaded
      if (!this.tooltipContent && !this.loading) {
        await this.loadTooltip();
      }
      
      this.visible = true;
      
      // Only emit the visibility change event
      this.$emit('visibility-change', true);
    },
    
    onHoverEnd() {
      this.hover = false;
      this.visible = false;
      
      // Only emit the visibility change event
      this.$emit('visibility-change', false);
    },
    async loadTooltip() {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.get(`/api/tooltips/identifier/${this.identifier}`);
        this.tooltipContent = response.data.content || 'Tooltip content not available';
      } catch (err) {
        console.error('Error loading tooltip:', err);
        // If tooltip not found (404), show a friendly message instead of error
        if (err.response && err.response.status === 404) {
          this.tooltipContent = 'Tooltip content is not yet available for this field.';
        } else {
          this.error = 'Failed to load information';
          this.tooltipContent = '';
        }
      } finally {
        this.loading = false;
      }
    },
  },
   mounted() {
    // Initialize DOMPurify hooks if needed
    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
      // Set all links to open in new tab and add noopener/noreferrer
      if (node.tagName === 'A') {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
      
      // Add styling to images
      if (node.tagName === 'IMG') {
        node.setAttribute('style', 'max-width: 100%; height: auto;');
        node.setAttribute('class', 'rich-text-image');
      }
    });
  },
}
</script>

<style scoped>
  .relative.inline-flex.items-center {
    position: relative;
  }
</style>

<style>
/* Non-scoped styles for tooltip - higher specificity */
.info-tooltip {
  position: fixed !important;
  z-index: 2147483647 !important;
  pointer-events: none !important;
  max-width: 300px !important;
  min-width: 200px !important;
}

.info-tooltip .info-tooltip-content {
  background-color: #374151 !important;
  border: 2px solid #4b5563 !important;
  border-radius: 0.5rem !important;
  padding: 0.75rem !important;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2) !important;
  backdrop-filter: blur(12px) !important;
  font-size: 0.75rem !important;
  line-height: 1.5 !important;
  color: #ffffff !important;
  opacity: 1 !important;
  max-height: 400px !important;
  overflow-y: auto !important;
  word-wrap: break-word !important;
  white-space: normal !important;
}

/* Dark mode support */
.dark .info-tooltip .info-tooltip-content {
  background-color: #1f2937 !important;
  border-color: #374151 !important;
  color: #f3f4f6 !important;
}
</style>

